import React, { useEffect, useState } from "react";
import { Box, Button } from "@material-ui/core";
import {
  CTextField,
  CSelect,
  CAutocomplete,
  HFlex,
  ProgressButton,
  Text,
  DialogSuccessNotice,
  useErrorContext,
} from "shared";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useStates } from "shared/ValueTable";
import {
  useCreateHealthCareOrganization,
  useUpdateHealthCareOrganization,
  useCountries,
  HealthCareOrganization,
} from "../queries/useHealthCareOrganizations";
import { isUsMarket } from "utils/market";

interface Props {
  organization?: HealthCareOrganization;
  onSuccess: (created?: HealthCareOrganization) => void;
  onCancel: () => void;
}

interface FormData {
  name: string;
  contact_email: string;
  billing_email: string;
  address: string;
  city: string;
  zip_code: string;
  iana_timezone: string;
  organization_size_range: string;
  platform_fee_model: string;
  platform_fee_cents: string;
  platform_fee_percentage: string;
  seats: string;
  country_id: number | null;
  us_state_id: number | null;
}

const timezoneOptions = [
  { value: "", label: "None" },
  { value: "America/New_York", label: "America/New_York" },
  { value: "America/Chicago", label: "America/Chicago" },
  { value: "America/Denver", label: "America/Denver" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles" },
  { value: "America/Phoenix", label: "America/Phoenix" },
  { value: "America/Anchorage", label: "America/Anchorage" },
  { value: "Pacific/Honolulu", label: "Pacific/Honolulu" },
  { value: "Europe/Stockholm", label: "Europe/Stockholm" },
  { value: "Europe/London", label: "Europe/London" },
  { value: "Europe/Paris", label: "Europe/Paris" },
  { value: "Europe/Berlin", label: "Europe/Berlin" },
];

const organizationSizeRangeOptions = [
  { value: "size_1_10", label: "1–10" },
  { value: "size_11_25", label: "11–25" },
  { value: "size_26_50", label: "26–50" },
  { value: "size_51_100", label: "51–100" },
  { value: "size_101_250", label: "101–250" },
  { value: "size_251_500", label: "251–500" },
  { value: "size_501_1000", label: "501–1000" },
  { value: "size_1001_plus", label: "1001+" },
];

const platformFeeModelOptions = [
  { value: "flat_fee", label: "Flat Fee" },
  { value: "per_seat", label: "Per Seat" },
  { value: "percentage", label: "Percentage" },
];

export const HealthCareOrganizationForm: React.VFC<Props> = ({ organization, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const setError = useErrorContext();
  const [successData, setSuccessData] = useState<{ heading: string; body: string } | null>(null);
  const states = useStates();
  const countriesQuery = useCountries();
  const countries = countriesQuery.data ?? [];
  const usCountry = countries.find(c => c.country_code === "US");
  const defaultCountry = countries.find(c => c.country_code === (isUsMarket ? "US" : "SE"));

  const isEditing = !!organization;

  const form = useForm<FormData>({
    defaultValues: {
      name: organization?.name || "",
      contact_email: organization?.contact_email || "",
      billing_email: organization?.billing_email || "",
      address: organization?.address || "",
      city: organization?.city || "",
      zip_code: organization?.zip_code || "",
      iana_timezone: organization?.iana_timezone || "",
      organization_size_range: organization?.organization_size_range || "",
      platform_fee_model: "",
      platform_fee_cents: "",
      platform_fee_percentage: "",
      seats: "0",
      country_id: organization?.country_id ?? defaultCountry?.id ?? null,
      us_state_id: organization?.us_state_id ?? null,
    },
  });

  useEffect(() => {
    if (defaultCountry && !form.getValues("country_id")) {
      form.setValue("country_id", defaultCountry.id);
    }
  }, [defaultCountry, form]);

  // Pricing fields are only relevant during creation
  const platformFeeModel = useWatch({ control: form.control, name: "platform_fee_model" });
  const isPercentage = platformFeeModel === "percentage";
  const isPerSeat = platformFeeModel === "per_seat";

  const stateOptions = (states.data ?? []).map(s => ({ value: s.id as number, label: `${s.value} (${s.code})` }));

  const createMutation = useCreateHealthCareOrganization();
  const updateMutation = useUpdateHealthCareOrganization();

  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  const submit = form.handleSubmit(async fields => {
    try {
      if (isEditing) {
        await updateMutation.mutateAsync({
          id: organization.id,
          name: fields.name,
          contact_email: fields.contact_email || undefined,
          billing_email: fields.billing_email || undefined,
          address: fields.address || undefined,
          city: fields.city || undefined,
          zip_code: fields.zip_code || undefined,
          iana_timezone: fields.iana_timezone || undefined,
          organization_size_range: fields.organization_size_range || undefined,
          country_id: fields.country_id ?? usCountry?.id ?? undefined,
          us_state_id: fields.us_state_id ?? undefined,
          platform_fee_model: organization.platform_fee_model ?? undefined,
          platform_fee_cents: organization.platform_fee_cents ?? undefined,
          platform_fee_bps: organization.platform_fee_bps ?? undefined,
          seats: organization.seats,
        });
        setSuccessData({
          heading: t("form.hco_updated_successfully"),
          body: `${organization.name} (ID: ${organization.id})`,
        });
      } else {
        const created = await createMutation.mutateAsync({
          name: fields.name,
          contact_email: fields.contact_email || undefined,
          billing_email: fields.billing_email || undefined,
          address: fields.address || undefined,
          city: fields.city || undefined,
          zip_code: fields.zip_code || undefined,
          iana_timezone: fields.iana_timezone || undefined,
          organization_size_range: fields.organization_size_range || undefined,
          platform_fee_model: fields.platform_fee_model || undefined,
          platform_fee_cents: isPercentage
            ? 0
            : isPerSeat
            ? 0
            : fields.platform_fee_cents
            ? Number(fields.platform_fee_cents)
            : 0,
          platform_fee_bps: isPercentage
            ? fields.platform_fee_percentage
              ? Math.round(Number(fields.platform_fee_percentage) * 100)
              : 0
            : 0,
          country_id: fields.country_id ?? usCountry?.id ?? undefined,
          us_state_id: fields.us_state_id ?? undefined,
          seats: isPerSeat && fields.seats ? Number(fields.seats) : 0,
        });
        setSuccessData({
          heading: t("form.hco_created_successfully"),
          body: `${created.name} (ID: ${created.id})`,
        });
      }
    } catch (err) {
      setError(
        isEditing
          ? t("errors.could_not_update_health_care_organization")
          : t("errors.could_not_create_health_care_organization"),
        err
      );
    }
  });

  if (successData)
    return <DialogSuccessNotice heading={successData.heading} body={successData.body} onClose={() => onSuccess()} />;

  return (
    <Box mx={3}>
      <Text heading bold mb={3}>
        {isEditing ? t("form.edit_health_care_organization") : t("form.create_health_care_organization")}
      </Text>
      <FormProvider {...form}>
        <form onSubmit={submit}>
          <CTextField required name="name" autoFocus label={t("form.name")} margin="normal" size="small" fullWidth />

          <CTextField
            required
            name="contact_email"
            label={t("form.hco_contact_email")}
            type="email"
            margin="normal"
            size="small"
            fullWidth
          />

          <CTextField
            required
            name="billing_email"
            label={t("form.hco_billing_email")}
            type="email"
            margin="normal"
            size="small"
            fullWidth
          />

          <CTextField
            required
            name="address"
            label={t("form.hco_address")}
            margin="normal"
            size="small"
            fullWidth
          />

          <CTextField
            required
            name="city"
            label={t("form.hco_city")}
            margin="normal"
            size="small"
            fullWidth
          />

          <CTextField
            required
            name="zip_code"
            label={t("form.hco_zip_code")}
            margin="normal"
            size="small"
            fullWidth
          />

          <CSelect
            name="iana_timezone"
            label={t("settings.profile_information.time_zone")}
            items={timezoneOptions}
            margin="normal"
            size="small"
            fullWidth
          >
            {({ label }) => <Text>{label}</Text>}
          </CSelect>

          <CSelect
            required
            name="organization_size_range"
            label={t("form.hco_organization_size_range")}
            items={organizationSizeRangeOptions}
            margin="normal"
            size="small"
            fullWidth
          >
            {({ label }) => <Text>{label}</Text>}
          </CSelect>

          {!isEditing && (
            <>
              <CSelect
                required
                name="platform_fee_model"
                label={t("form.hco_platform_fee_model")}
                items={platformFeeModelOptions}
                margin="normal"
                size="small"
                fullWidth
              >
                {({ label }) => <Text>{label}</Text>}
              </CSelect>

              {isPercentage && (
                <CTextField
                  name="platform_fee_percentage"
                  label={t("form.hco_platform_fee_percentage")}
                  type="number"
                  margin="normal"
                  size="small"
                  fullWidth
                  inputProps={{ step: "0.01", min: "0", max: "100" }}
                />
              )}

              {!isPercentage && !isPerSeat && (
                <CTextField
                  name="platform_fee_cents"
                  label={t("form.hco_platform_fee_cents")}
                  type="number"
                  margin="normal"
                  size="small"
                  fullWidth
                />
              )}

              {isPerSeat && (
                <>
                  <Text secondary caption mt={1} mb={1}>
                    {t("form.hco_per_seat_subscription_note")}
                  </Text>
                  <CTextField
                    name="seats"
                    label={t("form.hco_seats")}
                    type="number"
                    margin="normal"
                    size="small"
                    fullWidth
                  />
                </>
              )}
            </>
          )}

          <CSelect
            name="country_id"
            label={t("form.hco_country")}
            items={countries.map(c => ({ value: c.id, label: c.name }))}
            margin="normal"
            size="small"
            fullWidth
            disabled={isUsMarket}
          >
            {({ label }) => <Text>{label}</Text>}
          </CSelect>

          {form.watch("country_id") === usCountry?.id && (
            <CAutocomplete
              required
              name="us_state_id"
              label={t("form.hco_state")}
              options={stateOptions}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
              margin="normal"
            />
          )}

          <HFlex justifyContent="flex-end" gap={2} my={4}>
            <Button disabled={isLoading} onClick={onCancel}>
              {t("buttons.cancel")}
            </Button>
            <ProgressButton loading={isLoading}>
              {isEditing ? t("buttons.save") : t("buttons.create")}
            </ProgressButton>
          </HFlex>
        </form>
      </FormProvider>
    </Box>
  );
};